import Axios from "axios"

import { getLang } from "lib/i18n"

import { getClientId, getRedirectUri } from "./auth"

// メール + パスワード認証（auth-api #1192 / P-3 の `/oauth/password/*`）。
//
// lib/api.ts を通さないのは、401 / 422 以外のステータス（未確認の 403、
// レート制限の 429）と 422 の本文（`{ errors: { email: "..." } }`）を
// 画面が見分ける必要があるため。lib/api.ts はそれらを UnknownNetworkError
// に丸めてしまう。結果は例外ではなく値で返す（target: es5 では
// instanceof の結果がビルド系によって変わりうるため）。
export type PasswordAuthResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; status: number | null; errors: Record<string, string> }

const request = async <T>(
  method: "post" | "put",
  path: string,
  data: Record<string, unknown>,
): Promise<PasswordAuthResult<T>> => {
  try {
    const res = await Axios[method]<T>(
      `${process.env.REACT_APP_API_URL}/${path}`,
      data,
      { headers: { Accept: "application/vnd.spearly.v2+json" } },
    )
    return { ok: true, data: res.data }
  } catch (err) {
    if (Axios.isAxiosError(err) && err.response) {
      const body = err.response.data as { errors?: Record<string, string> }
      return {
        ok: false,
        status: err.response.status,
        errors: body && body.errors ? body.errors : {},
      }
    }
    return { ok: false, status: null, errors: {} }
  }
}

// User#locale には、プロフィール更新（actions/profile.ts）と同じ画面の言語の値
// （ja / en_us / pt_br）をそのまま入れる。ログイン後の `setLang(profile.locale)` が
// この値を読むため、`en` や `pt_BR` を入れると画面の文言がすべて `::key::` になる。
// メールの言語は auth-api の MailLocale が en_us → en、pt_br → pt_BR に読み替える。
const apiLocale = (): string => getLang()

type PasswordSignupPayload = {
  email: string
  password: string
}

export const postPasswordSignup = (payload: PasswordSignupPayload) =>
  request<{ confirmation_required: boolean }>("post", "oauth/password/signup", {
    email: payload.email,
    password: payload.password,
    locale: apiLocale(),
  })

type PasswordSigninPayload = {
  email: string
  password: string
}

// 成功すると authorization code 付きの redirect_uri が返る。OAuth と同じく
// /login/callback で code を token に交換する。
export const postPasswordSignin = (payload: PasswordSigninPayload) =>
  request<{ redirect_uri: string }>("post", "oauth/password/signin", {
    email: payload.email,
    password: payload.password,
    client_id: getClientId(),
    redirect_uri: getRedirectUri(),
  })

export const postPasswordResetRequest = (email: string) =>
  request("post", "oauth/password/reset", { email })

type PutPasswordResetPayload = {
  resetPasswordToken: string
  password: string
  passwordConfirmation: string
}

export const putPasswordReset = (payload: PutPasswordResetPayload) =>
  request("put", "oauth/password/reset", {
    reset_password_token: payload.resetPasswordToken,
    password: payload.password,
    password_confirmation: payload.passwordConfirmation,
  })

export const postPasswordConfirmationResend = (email: string) =>
  request("post", "oauth/password/confirmation", { email })

export const putPasswordConfirmation = (confirmationToken: string) =>
  request("put", "oauth/password/confirmation", {
    confirmation_token: confirmationToken,
  })
